import React, { ChangeEvent, useState } from 'react';
import {
  Box,
  FormLabel,
  IconButton,
  LinearProgress,
  TextField,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import CustomPopover from './CustomPopover';
import Images from '../Utils/images';
import { notify } from '../Utils/toastify';
import { calculateFileSize } from '../helpers/functions';
import { uploadToS3 } from '../Utils/AwsService';
interface Props {
  id: string;
  label?: string;
  placeholder?: string;
  name: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  // value?: string | number;
  defaultValue?: string | number;
  variant?: 'outlined' | 'standard' | 'filled' | undefined;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?:
    | React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
    | undefined;
  value?: string;
  startAdornment?: React.ReactElement | null;
  endAdornment?: React.ReactElement | null;
  control?: null | any;
  tooltip?: boolean;
  popoverContent?: React.ReactElement | null;
  endAdornmentIcon?: React.ReactElement;
  setValue?: any;
  sizeLimit?: number;
  sizeLimitError?: string;
  showCard?: boolean;
  s3Upload?: boolean;
  s3fileUrl?: string;
  setS3FileUrl?: (url: string) => void;
  requiredField?: boolean;
  schema?: any;
}

/**
 * A custom FileInput component for handling file uploads.
 * @component
 * @example
 *   <FileInput
 *     id="file-upload"
 *     label="Upload your image"
 *     placeholder="Select a file"
 *     name="picture"
 *     helperText="Accepted formats: jpg, jpeg, png"
 *     disabled={false}
 *     error={false}
 *     variant="outlined"
 *     control={control}
 *     setValue={setValue}
 *     s3Upload={true}
 *     setS3FileUrl={setS3FileUrl}
 *     schema={schema}
 *   />
 * @prop {string} id - Unique identifier for the input field.
 * @prop {string} label - Text label displayed above the file input.
 * @prop {string} placeholder - Text shown inside the input when empty.
 * @prop {string} name - Name attribute for the file input.
 * @prop {string} helperText - Guidance text displayed below the input field.
 * @prop {boolean} error - Flag indicating if there is a validation error.
 * @prop {boolean} disabled - Disables the input field if set to true.
 * @prop {string} defaultValue - Sets the default initial value of the input.
 * @prop {string} variant - Specifies the style variant of the input.
 * @prop {string} value - The current value of the input field.
 * @prop {function} onBlur - Function called when the input loses focus.
 * @prop {element} startAdornment - Element placed at the start of the text field.
 * @prop {element} endAdornment - Element placed at the end of the text field.
 * @prop {function} onChange - Function called whenever the input value changes.
 * @prop {object} control - React Hook Form control object for managing the form state.
 * @prop {string} tooltip - Text displayed in a tooltip.
 * @prop {element} popoverContent - Content displayed within a popover.
 * @prop {element} endAdornmentIcon - Icon displayed at the end of the input field.
 * @prop {function} setValue - Function for updating the value of the form field.
 * @prop {number} [sizeLimit=3145728] - Maximum allowed file size in bytes (default is 3MB).
 * @prop {boolean} [showCard=false] - Flag to display detailed card view for uploaded file.
 * @prop {string} [sizeLimitError="File size exceed"] - Error message for exceeding file size limit.
 * @prop {boolean} [s3Upload=false] - Flag indicating S3 upload functionality.
 * @prop {function} setS3FileUrl - Function to set the uploaded file's S3 URL.
 * @prop {string} s3fileUrl - URL of the uploaded file on S3.
 * @prop {boolean} requiredField - Flag to indicate if the field is required.
 * @prop {object} schema - Validation schema to ensure the uploaded file meets specific requirements.
 * @description
 *   - Uploads via a backend-issued presigned URL when `s3Upload` is true, leveraging `setS3FileUrl` for updating the file URL.
 *   - Implements validation logic defined within the `schema` prop to provide robust file integrity and type checking.
 *   - Provides a visual progress indicator during file uploads, updating the user with upload percentage completion.
 *   - Clears the previous file URL locally when a new file is selected; the previous upload is left in S3 (no client-side delete-by-key path).
 */
const FileInput: React.FC<Props> = props => {
  const {
    id,
    label,
    placeholder,
    name,
    helperText,
    error,
    disabled,
    defaultValue,
    variant,
    startAdornment,
    control,
    tooltip,
    popoverContent,
    endAdornmentIcon,
    setValue,
    sizeLimit = 3 * 1024 * 1024,
    showCard = false,
    sizeLimitError = 'File size exceed',
    s3Upload = false,
    setS3FileUrl,
    s3fileUrl,
    requiredField,
    schema,
  } = props;
  const [progress, setProgress] = useState(0);

  const imageRef = React.useRef<HTMLInputElement>(null);
  const handleClick = () => {
    if (imageRef && imageRef.current) {
      imageRef.current.click();
    }
  };

  /**
   * Uploads a file via a backend-issued presigned URL and tracks upload progress.
   * @param {File} file - The file to be uploaded.
   * @returns {void} Does not return any value.
   * @description
   *   - Sets S3 file URL state upon successful upload.
   *   - Shows a notification and clears the input value in case of an upload error.
   */
  const handleUpload = async (file: File) => {
    const uploaded = await uploadToS3(file, progressValue => {
      setProgress(progressValue);
    });
    if (uploaded && setS3FileUrl) {
      setS3FileUrl(uploaded.url);
    } else if (!uploaded) {
      setValue(name, undefined);
    }
  };

  const handleDelete = () => {
    if (s3fileUrl) {
      setS3FileUrl && setS3FileUrl('');
    }
    setValue(name, undefined);
  };

  return (
    <Box>
      <FormLabel className="formLabel">
        {label} {requiredField ? <sup>*</sup> : null}
        {tooltip ? (
          <>
            <CustomPopover
              id=""
              children={popoverContent}
              arrow
              anchorOrigin_vertical="top"
              anchorOrigin_horizontal="center"
              transformOrigin_vertical="bottom"
              transformOrigin_horizonral="center"
            />
          </>
        ) : null}
      </FormLabel>
      {control ? (
        <Controller
          control={control}
          name={name}
          render={({ field }) => {
            return (
              <div className="fileSelection">
                <input
                  name="picture"
                  type="file"
                  ref={imageRef}
                  aria-hidden="true"
                  style={{ display: 'none' }}
                  onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                    if (e.target.files) {
                      if (s3fileUrl) {
                        handleDelete();
                      }
                      if (e.target.files[0].size <= sizeLimit) {
                        if (setValue) {
                          setValue(name, e.target.files[0]);
                        }
                        if (schema) {
                          const file = e.target.files[0];
                          try {
                            await schema.validateAt(name, { [name]: file });
                          } catch (error: any) {
                            error &&
                              error.message &&
                              notify(error.message, 'error');
                            console.error('Error uploading files:', error);
                            setValue(name, null);
                            return;
                          }
                        }

                        if (s3Upload) {
                          handleUpload(e.target.files[0]);
                        }
                      } else {
                        notify(sizeLimitError, 'error');
                        return;
                      }
                    }
                  }}
                  accept=".jpg , .jpeg , .png ,"
                />
                <TextField
                  id={id}
                  type="text"
                  helperText={helperText}
                  variant={variant}
                  error={error}
                  placeholder={placeholder}
                  disabled={disabled}
                  color="primary"
                  size="small"
                  {...field}
                  value={field.value ? field.value.name : ''}
                  // onChange={onChange}
                  // onClick={handleClick}
                  fullWidth
                  // InputLabelProps={{
                  //   shrink: true,
                  // }}
                  onClick={handleClick}
                  InputProps={{
                    startAdornment,
                    autoComplete: 'off',
                    endAdornment: endAdornmentIcon,
                    readOnly: true,
                  }}
                  className="fileInputField"
                />
                {field.value && showCard && (
                  <div className="fileProgress">
                    <div className="progressInfo">
                      <div className="fileIc">
                        <img src={Images.FILE_ICON} alt="File" />
                      </div>
                      <div className="doc_name">
                        <h4>{field.value ? field.value.name : ''}</h4>
                        <p>{calculateFileSize(field.value)}</p>
                      </div>
                      <IconButton className="icButton" onClick={handleDelete}>
                        <img src={Images.DELETE_IC} alt="Delete" />
                      </IconButton>
                    </div>
                    <div className="linearProgress">
                      <LinearProgress variant="determinate" value={progress} />
                      <p>{progress}%</p>
                    </div>
                  </div>
                )}
              </div>
            );
          }}
        />
      ) : (
        <>
          <input
            name="picture"
            type="file"
            ref={imageRef}
            aria-hidden="true"
            style={{ display: 'none' }}
            accept=".jpg , .jpeg , .png , .svg , .webp"
          />
          <TextField
            id={id}
            size="small"
            name={name}
            type="text"
            helperText={helperText}
            variant={variant}
            error={error}
            placeholder={placeholder}
            disabled={disabled}
            color="primary"
            // value={value || ''}
            // onChange={onChange}
            defaultValue={defaultValue}
            fullWidth
            onClick={handleClick}
            InputProps={{
              startAdornment,
              autoComplete: 'off',
              endAdornment: endAdornmentIcon,
              readOnly: true,
            }}
            className="fileInputField"
          />
        </>
      )}
    </Box>
  );
};

FileInput.defaultProps = {
  placeholder: '',
  error: false,
  disabled: false,
  variant: 'outlined',
  startAdornment: null,
  endAdornment: null,
};

export default FileInput;
